import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TestClockContextType {
  isTestMode: boolean;
  frozenTime: Date | null;
  clockOffset: number;
  isEnabled: boolean;
  setIsEnabled: (enabled: boolean) => void;
  openCheckoutInNewTab: boolean;
  setOpenCheckoutInNewTab: (newTab: boolean) => void;
  updateTestClock: (newTime: string) => void;
  resetTestClock: () => void;
  getCurrentTime: () => Date;
}

const TestClockContext = createContext<TestClockContextType | undefined>(undefined);

export function TestClockProvider({ children }: { children: ReactNode }) {
  const [frozenTime, setFrozenTime] = useState<Date | null>(null);
  const [clockOffset, setClockOffset] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Load persisted isEnabled state from localStorage
  const [isEnabled, setIsEnabled] = useState(() => {
    const saved = localStorage.getItem('test-clock-enabled');
    return saved !== null ? saved === 'true' : true;
  });
  
  const [openCheckoutInNewTab, setOpenCheckoutInNewTab] = useState(false);

  // Check admin role on mount
  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin'
        });

        if (!error && data === true) {
          setIsAdmin(true);
        }
      } catch (error) {
        // Silent fail - admin check is non-critical
      }
    };
    checkAdminRole();
  }, []);

  const loadAdminSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: settings } = await supabase
        .from('admin_settings')
        .select('open_checkout_in_new_tab')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settings) {
        setOpenCheckoutInNewTab(settings.open_checkout_in_new_tab);
      }
    } catch (error) {
      console.error('[TestClock] Failed to load admin settings:', error);
    }
  };

  const loadTestClockState = async () => {
    try {
      const { data } = await supabase
        .from('subscriptions')
        .select('stripe_customer_id')
        .eq('status', 'active')
        .single();

      if (data?.stripe_customer_id) {
        const { data: clockData } = await supabase.functions.invoke('get-test-clock-time', {
          body: { stripe_customer_id: data.stripe_customer_id }
        });

        if (clockData?.frozen_time) {
          const frozen = new Date(clockData.frozen_time);
          setFrozenTime(frozen);
          setClockOffset(frozen.getTime() - Date.now());
        }
      }
    } catch (error) {
      console.error('[TestClock] Failed to load test clock:', error);
    }
  };

  const updateTestClock = (newTime: string) => {
    const newFrozen = new Date(newTime);
    setFrozenTime(newFrozen);
    setClockOffset(newFrozen.getTime() - Date.now());
  };

  const resetTestClock = () => {
    setFrozenTime(null);
    setClockOffset(0);
  };

  const getCurrentTime = () => {
    if (frozenTime && isAdmin && isEnabled) {
      return new Date(Date.now() + clockOffset);
    }
    return new Date();
  };

  const saveCheckoutSetting = async (newValue: boolean) => {
    setOpenCheckoutInNewTab(newValue);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('admin_settings')
        .upsert({
          user_id: user.id,
          open_checkout_in_new_tab: newValue,
          updated_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('[TestClock] Failed to save checkout setting:', error);
    }
  };

  // Persist isEnabled state to localStorage
  const handleSetIsEnabled = (enabled: boolean) => {
    setIsEnabled(enabled);
    localStorage.setItem('test-clock-enabled', String(enabled));
  };

  return (
    <TestClockContext.Provider value={{
      isTestMode: isAdmin && frozenTime !== null && isEnabled,
      frozenTime,
      clockOffset,
      isEnabled,
      setIsEnabled: handleSetIsEnabled,
      openCheckoutInNewTab,
      setOpenCheckoutInNewTab: saveCheckoutSetting,
      updateTestClock,
      resetTestClock,
      getCurrentTime
    }}>
      {children}
    </TestClockContext.Provider>
  );
}

export const useTestClock = () => {
  const context = useContext(TestClockContext);
  if (context === undefined) {
    throw new Error('useTestClock must be used within TestClockProvider');
  }
  return context;
};
